import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { Gauge, Smartphone, Monitor, RefreshCw, CheckCircle2, Wrench, Copy, Zap, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useServerFn } from "@tanstack/react-start";
import { sb } from "@/lib/sb";
import { runPageSpeed } from "@/lib/pagespeed.functions";

export const Route = createFileRoute("/_authenticated/dashboard/admin/speed")({
  component: SpeedPage,
});

const SITE_URL = "https://www.homesteadconstruction.us";

function scoreColor(s: number) {
  if (s >= 90) return "text-emerald-600 border-emerald-500/30 bg-emerald-500/10";
  if (s >= 50) return "text-yellow-700 border-yellow-500/30 bg-yellow-500/10";
  return "text-destructive border-destructive/30 bg-destructive/10";
}

function LighthousePanel() {
  const psi = useServerFn(runPageSpeed);
  const [url, setUrl] = useState(SITE_URL);
  const [strategy, setStrategy] = useState<"mobile" | "desktop">("mobile");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<Awaited<ReturnType<typeof runPageSpeed>> | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const run = async () => {
    setLoading(true); setErr(null); setResult(null);
    try {
      const r = await psi({ data: { url, strategy } });
      setResult(r);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="font-display font-semibold flex items-center gap-2 text-ink">
          <Zap size={16} /> Lighthouse (PageSpeed Insights)
        </h3>
      </div>
      <div className="flex gap-2 flex-wrap">
        <input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          className="flex-1 min-w-[240px] px-3 py-1.5 rounded-lg bg-background border border-border text-sm"
          placeholder="https://example.com/page"
        />
        <select
          value={strategy}
          onChange={(e) => setStrategy(e.target.value as "mobile" | "desktop")}
          className="px-3 py-1.5 rounded-lg bg-background border border-border text-sm"
        >
          <option value="mobile">Mobile</option>
          <option value="desktop">Desktop</option>
        </select>
        <button
          onClick={run}
          disabled={loading}
          className="px-4 py-1.5 rounded-lg bg-brand text-primary-foreground text-sm font-semibold disabled:opacity-60 flex items-center gap-2"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />} Run scan
        </button>
      </div>
      {err && <p className="text-xs text-destructive break-words">{err}</p>}
      {result && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {([
              ["Performance", result.scores.performance],
              ["Accessibility", result.scores.accessibility],
              ["Best Practices", result.scores.bestPractices],
              ["SEO", result.scores.seo],
            ] as const).map(([label, score]) => (
              <div key={label} className={`border rounded-lg p-3 ${scoreColor(score)}`}>
                <div className="text-xs opacity-80">{label}</div>
                <div className="font-display text-2xl font-bold">{score}</div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {Object.entries(result.metrics).map(([k, v]) => (
              <div key={k} className="bg-background border border-border rounded-lg p-2">
                <div className="text-xs text-muted-foreground">{k}</div>
                <div className="font-semibold text-sm">{v.display}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

type Row = {
  metric: string;
  value: number;
  page_url: string;
  device: string;
  ts: string;
};

const metricInfo: Record<
  string,
  { label: string; unit: string; good: number; poor: number; help: string }
> = {
  LCP: {
    label: "Largest Contentful Paint",
    unit: "s",
    good: 2500,
    poor: 4000,
    help: "How quickly the biggest thing on the page shows up. Lower is better.",
  },
  INP: {
    label: "Interaction Response",
    unit: "ms",
    good: 200,
    poor: 500,
    help: "How fast the site reacts to a tap or click.",
  },
  CLS: {
    label: "Layout Shift",
    unit: "",
    good: 0.1,
    poor: 0.25,
    help: "How much content jumps around while loading. Closer to 0 is better.",
  },
  FCP: {
    label: "First Paint",
    unit: "s",
    good: 1800,
    poor: 3000,
    help: "When something — anything — first appears on screen.",
  },
  TTFB: {
    label: "Server Response",
    unit: "ms",
    good: 800,
    poor: 1800,
    help: "How fast the server starts sending the page back.",
  },
};

const fmt = (m: string, v: number) => {
  if (m === "CLS") return v.toFixed(3);
  if (metricInfo[m]?.unit === "s") return (v / 1000).toFixed(2) + "s";
  return Math.round(v) + "ms";
};

const ratingFor = (m: string, v: number) => {
  const i = metricInfo[m];
  if (!i) return "—";
  if (v <= i.good) return "good";
  if (v <= i.poor) return "needs-work";
  return "poor";
};

const ratingClass = (r: string) =>
  r === "good"
    ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/30"
    : r === "needs-work"
      ? "bg-yellow-500/10 text-yellow-700 border-yellow-500/30"
      : r === "poor"
        ? "bg-destructive/10 text-destructive border-destructive/30"
        : "bg-muted text-muted-foreground border-border";

const percentile = (vals: number[], p: number) => {
  if (!vals.length) return 0;
  const sorted = [...vals].sort((a, b) => a - b);
  return sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * p))];
};

function SpeedPage() {
  const [rows, setRows] = useState<Row[]>([]);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const since = new Date(Date.now() - days * 24 * 3600 * 1000).toISOString();
    const { data } = await sb
      .from("web_vitals")
      .select("metric, value, page_url, device, ts")
      .gte("ts", since)
      .limit(5000);
    setRows((data as Row[]) ?? []);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const byMetric = (m: string, dev?: string) =>
    rows.filter((r) => r.metric === m && (!dev || r.device === dev));
  const p75 = (m: string, dev?: string) => percentile(byMetric(m, dev).map((r) => r.value), 0.75);

  const perPage = useMemo(() => {
    const pages = Array.from(
      new Set(rows.filter((r) => r.metric === "LCP").map((r) => r.page_url)),
    );
    return pages
      .map((p) => ({
        p,
        lcp: percentile(
          rows.filter((r) => r.metric === "LCP" && r.page_url === p).map((r) => r.value),
          0.75,
        ),
        cls: percentile(
          rows.filter((r) => r.metric === "CLS" && r.page_url === p).map((r) => r.value),
          0.75,
        ),
        inp: percentile(
          rows.filter((r) => r.metric === "INP" && r.page_url === p).map((r) => r.value),
          0.75,
        ),
        samples: rows.filter((r) => r.metric === "LCP" && r.page_url === p).length,
      }))
      .sort((a, b) => b.lcp - a.lcp);
  }, [rows]);

  const suggestions = useMemo(() => {
    const hint: Record<string, string> = {
      LCP: "Optimize the main image, preload critical media, and reduce render-blocking assets.",
      INP: "Reduce long tasks, defer non-critical scripts, and keep click handlers lightweight.",
      CLS: "Reserve space for images and embeds so the layout does not jump during load.",
    };

    return perPage.flatMap((page) => {
      const metrics = [
        { key: "LCP", value: page.lcp },
        { key: "INP", value: page.inp },
        { key: "CLS", value: page.cls },
      ] as const;

      return metrics
        .filter((metric) => metric.value && ratingFor(metric.key, metric.value) !== "good")
        .map((metric) => ({
          metric: metric.key,
          page: page.p,
          value: metric.value,
          rating: ratingFor(metric.key, metric.value),
          prompt: `Improve ${metric.key} on ${page.p}. Current p75 is ${fmt(metric.key, metric.value)}. ${hint[metric.key]}`,
        }));
    });
  }, [perPage]);

  const copyAll = async () => {
    if (!suggestions.length) return;
    await navigator.clipboard.writeText(
      [
        "Please improve Core Web Vitals on Homestead Construction:",
        ...suggestions.map(
          (suggestion, index) =>
            `${index + 1}. [${suggestion.rating.toUpperCase()}] ${suggestion.metric} on ${suggestion.page} = ${fmt(suggestion.metric, suggestion.value)}\n   ${suggestion.prompt}`,
        ),
      ].join("\n"),
    );
    toast.success("All fix prompts copied.");
  };

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold text-ink flex items-center gap-2">
            <Gauge size={22} /> Speed Report
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Real-visitor performance. Numbers shown are p75 — 75% of visitors had this experience or better.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value, 10))}
            className="px-3 py-1.5 rounded-lg bg-card border border-border text-sm"
          >
            <option value={1}>Last 24 hours</option>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
          </select>
          <button
            onClick={load}
            className="p-1.5 rounded-lg bg-card border border-border text-sm hover:border-brand"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      <LighthousePanel />



      {rows.length === 0 && !loading && (
        <div className="bg-card border border-border rounded-xl p-8 text-center">
          <p className="text-muted-foreground">
            No speed data yet for this range. Visit the public site in a normal browser tab
            to start collecting measurements.
          </p>
        </div>
      )}

      {rows.length > 0 && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {(["LCP", "INP", "CLS", "FCP", "TTFB"] as const).map((m) => {
              const v = p75(m);
              const r = ratingFor(m, v);
              const info = metricInfo[m];
              return (
                <div key={m} className="bg-card border border-border rounded-xl p-4">
                  <div className="text-xs text-muted-foreground">{info.label}</div>
                  <div className="font-display text-2xl font-bold mt-1">
                    {v ? fmt(m, v) : "—"}
                  </div>
                  <span
                    className={`mt-2 inline-block text-xs px-2 py-0.5 rounded border ${ratingClass(r)}`}
                  >
                    {r}
                  </span>
                  <p className="text-xs text-muted-foreground mt-2 leading-snug">{info.help}</p>
                </div>
              );
            })}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { key: "mobile", icon: Smartphone, label: "Mobile visitors" },
              { key: "desktop", icon: Monitor, label: "Desktop visitors" },
            ].map(({ key, icon: Icon, label }) => (
              <div key={key} className="bg-card border border-border rounded-xl p-4">
                <h3 className="font-display font-semibold flex items-center gap-2 mb-3">
                  <Icon size={16} /> {label}
                </h3>
                <div className="grid grid-cols-3 gap-2 text-sm">
                  {(["LCP", "INP", "CLS"] as const).map((m) => {
                    const v = p75(m, key);
                    const r = ratingFor(m, v);
                    return (
                      <div key={m}>
                        <div className="text-xs text-muted-foreground">{m}</div>
                        <div className="font-semibold">{v ? fmt(m, v) : "—"}</div>
                        <span
                          className={`text-[10px] px-1.5 py-0.5 rounded border ${ratingClass(r)}`}
                        >
                          {r}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
              <h3 className="font-display font-semibold flex items-center gap-2 text-ink">
                <Wrench size={16} /> Suggested changes
                <span className="text-xs font-normal text-muted-foreground">({suggestions.length})</span>
              </h3>
              <button
                onClick={copyAll}
                disabled={!suggestions.length}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-brand text-primary-foreground text-xs font-semibold disabled:opacity-50"
              >
                <Copy size={12} /> Fix all in Lovable
              </button>
            </div>
            {suggestions.length === 0 ? (
              <div className="flex items-center gap-2 text-sm text-emerald-400">
                <CheckCircle2 size={16} /> No issues detected for real visitors right now.
              </div>
            ) : (
              <ul className="space-y-2">
                {suggestions.map((suggestion, index) => (
                  <li key={`${suggestion.page}-${suggestion.metric}-${index}`} className="flex items-start gap-3 p-3 rounded-lg border border-border bg-background">
                    <span className={`shrink-0 px-2 py-0.5 rounded border text-xs ${ratingClass(suggestion.rating)}`}>
                      {suggestion.rating}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-ink">
                        {suggestion.metric} on <span className="font-mono">{suggestion.page}</span> = {fmt(suggestion.metric, suggestion.value)}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 leading-snug">{suggestion.prompt}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="bg-card border border-border rounded-xl p-4">
            <h3 className="font-display font-semibold mb-3">By page</h3>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted-foreground border-b border-border">
                  <th className="py-2 font-medium">Page</th>
                  <th className="py-2 font-medium">LCP (p75)</th>
                  <th className="py-2 font-medium">INP (p75)</th>
                  <th className="py-2 font-medium">CLS (p75)</th>
                  <th className="py-2 font-medium text-right">Samples</th>
                </tr>
              </thead>
              <tbody>
                {perPage.map((row) => (
                  <tr key={row.p} className="border-b border-border/50">
                    <td className="py-2 font-medium">{row.p}</td>
                    <td className="py-2">
                      <span
                        className={`px-2 py-0.5 rounded border text-xs ${ratingClass(ratingFor("LCP", row.lcp))}`}
                      >
                        {fmt("LCP", row.lcp)}
                      </span>
                    </td>
                    <td className="py-2">
                      {row.inp ? (
                        <span
                          className={`px-2 py-0.5 rounded border text-xs ${ratingClass(ratingFor("INP", row.inp))}`}
                        >
                          {fmt("INP", row.inp)}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="py-2">
                      {row.cls ? (
                        <span
                          className={`px-2 py-0.5 rounded border text-xs ${ratingClass(ratingFor("CLS", row.cls))}`}
                        >
                          {fmt("CLS", row.cls)}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="py-2 text-right text-muted-foreground">{row.samples}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
